// ─────────────────────────────────────────────────────────────────────────────
// snap_manager.ts — Spatial index of hookable geometry for stroke snapping
//
// Registers vertices, centers, and edge midpoints from figure blocks.
// Provides fast nearest-target queries within a configurable radius.
// ─────────────────────────────────────────────────────────────────────────────

import type { WvFigureBlock } from './notebook_state';

// ── Public types ─────────────────────────────────────────────────────────────

export type SnapKind = 'vertex' | 'center' | 'midpoint';

export interface SnapTarget {
  x: number;
  y: number;
  kind: SnapKind;
  /** Source figure block id. */
  blockId: string;
  /** Vertex index (for 'vertex') or edge index (for 'midpoint'). */
  sourceIndex: number;
}

export interface SnapResult {
  target: SnapTarget;
  distance: number;
}

// ── Default radii per target kind ────────────────────────────────────────────
// Sized to be forgiving — the user shouldn't have to land within a pixel.
// Vertex hooks are the most common (line endpoints), so they get the
// widest grab; midpoints used to be tighter but mid-line connections are
// becoming a first-class gesture so they got widened too.

const DEFAULT_RADII: Record<SnapKind, number> = {
  vertex: 40,
  center: 40,
  midpoint: 36,
};

// ── Manager ──────────────────────────────────────────────────────────────────

export class SnapManager {
  private _targets: SnapTarget[] = [];
  private _radii: Record<SnapKind, number> = { ...DEFAULT_RADII };
  private _dirty = true;

  /** Replace the snap radius for a given target kind. */
  setRadius(kind: SnapKind, radius: number): void {
    this._radii[kind] = radius;
  }

  /** Mark the index as stale — will be rebuilt on next query. */
  invalidate(): void {
    this._dirty = true;
  }

  /**
   * Rebuild the spatial index from all figure blocks on the given page.
   * Call this lazily (when _dirty) or eagerly on figure mutations.
   */
  rebuild(blocks: Map<string, { kind: string } & Record<string, unknown>>): void {
    this._targets = [];
    for (const [bid, blk] of blocks) {
      if (blk.kind !== 'figure') continue;
      const fig = blk as unknown as WvFigureBlock;
      this._indexFigure(bid, fig);
    }
    this._dirty = false;
  }

  /**
   * Find the nearest snap target within radius of (x, y).
   * Returns null if nothing is close enough.
   * If `blocks` is provided and the index is dirty, rebuilds first.
   */
  findNearest(
    x: number, y: number,
    blocks?: Map<string, { kind: string } & Record<string, unknown>>,
  ): SnapResult | null {
    if (this._dirty && blocks) this.rebuild(blocks);

    let best: SnapResult | null = null;
    for (const t of this._targets) {
      const dx = x - t.x;
      const dy = y - t.y;
      const d = Math.sqrt(dx * dx + dy * dy);
      const r = this._radii[t.kind];
      if (d <= r && (best === null || d < best.distance)) {
        best = { target: t, distance: d };
      }
    }
    return best;
  }

  /** Read-only view of all indexed targets — for the meta-layer
   *  render pass (anchor dots, hover lantern). Rebuilds first if
   *  dirty and `blocks` was provided; otherwise returns the last
   *  built snapshot. */
  getTargets(
    blocks?: Map<string, { kind: string } & Record<string, unknown>>,
  ): readonly SnapTarget[] {
    if (this._dirty && blocks) this.rebuild(blocks);
    return this._targets;
  }

  // ── Internals ──────────────────────────────────────────────────────────

  private _indexFigure(blockId: string, fig: WvFigureBlock): void {
    // Vertices
    for (let i = 0; i < fig.vertices.length; i++) {
      const v = fig.vertices[i];
      this._targets.push({ x: v.x, y: v.y, kind: 'vertex', blockId, sourceIndex: i });
    }

    // Center (circle, ellipse, point)
    if (fig.center) {
      this._targets.push({ x: fig.center.x, y: fig.center.y, kind: 'center', blockId, sourceIndex: -1 });
    }

    // Edge midpoints
    for (let i = 0; i < fig.edges.length; i++) {
      const e = fig.edges[i];
      const a = fig.vertices[e.from];
      const b = fig.vertices[e.to];
      if (a && b) {
        this._targets.push({
          x: (a.x + b.x) / 2,
          y: (a.y + b.y) / 2,
          kind: 'midpoint',
          blockId,
          sourceIndex: i,
        });
      }
    }
  }
}
